import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { REQUIRE_LOGIN } from "../config";

/**
 * Gate for /app routes.
 *
 * By default, authentication is required only when the global REQUIRE_LOGIN
 * flag is on — so with the flag off, guests can reach read-only pages. Pass
 * `requireAuth` to force login regardless of the flag (used for private pages
 * like Messages).
 */
export default function ProtectedRoute({
  children,
  requireAuth,
}: {
  children: React.ReactNode;
  requireAuth?: boolean;
}) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const mustAuth = requireAuth ?? REQUIRE_LOGIN;
  if (!isAuthenticated && mustAuth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
